import { UNIT_PRICE_DECIMAL_PLACES } from '@/constants'
import { DateTime } from '@quotetome/materials-api'
import type { Order, PurchaseOrder, ReleaseRequest } from '@quotetome/materials-api'
import { useAuthStore } from '~/stores/auth'

type DateValue = DateTime | undefined | null | number
type NumberValue = string | number | null | undefined

function formatDate(date: DateValue, formatStr: string) {
  if (date && (date as DateTime).format) {
    return (date as DateTime).format(formatStr)
  }
  if (date) {
    return DateTime.fromUnix(date as number).format(formatStr)
  }
  return '—'
}

export const dateFormat = 'MMM D'
export const dateYearFormat = 'MMM D, YYYY'
export const dateTimeFormat = `${dateYearFormat}, h:mm A`
export const dateTimeNoYearFormat = 'MMM D, h:mm A'
export const isoDateFormat = 'YYYY-MM-DD'
export const onlyTimeFormat = 'h:mm A'

export const conciseDate = (date: DateValue) => {
  return formatDate(date, dateFormat)
}

const CURRENCY_BY_COUNTRY: Record<string, { locale: string; currency: string }> = {
  GB: { locale: 'en-GB', currency: 'GBP' },
}
const DEFAULT_CURRENCY = { locale: 'en-CA', currency: 'CAD' }

export const currency = (value: NumberValue, decimalPlaces = 2) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '—'
  }
  const country = useAuthStore().userCompany?.country
  const { locale, currency: currencyCode } = (country && CURRENCY_BY_COUNTRY[country]) || DEFAULT_CURRENCY
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: decimalPlaces,
    roundingMode: 'halfEven',
  }).format(Number(value))
}

export const dateMonth = (date: DateValue) => {
  return formatDate(date, dateFormat)
}

export const dateTime = (date: DateValue) => {
  return formatDate(date, dateTimeFormat)
}

export const dateTimeSmartYear = (date: DateValue) => {
  if (date && (date as DateTime).format) {
    const dt = date as DateTime
    const format = dt.isSame(new DateTime(), 'year') ? dateTimeNoYearFormat : dateTimeFormat
    return dt.format(format)
  }
  if (date) {
    const dt = DateTime.fromUnix(date as number)
    const format = dt.isSame(new DateTime(), 'year') ? dateTimeNoYearFormat : dateTimeFormat
    return dt.format(format)
  }
  return '—'
}

export const dateYear = (date: DateValue) => {
  return formatDate(date, dateYearFormat)
}

export const decimal = (
  value: NumberValue,
  { maximumFractionDigits } = { maximumFractionDigits: UNIT_PRICE_DECIMAL_PLACES },
) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'decimal',
    maximumFractionDigits,
    minimumFractionDigits: 2,
    roundingMode: 'halfEven',
  }).format(Number(value))
}

export const timeOrDateTime = (date: number | undefined | null) => {
  if (date) {
    const format = DateTime.fromUnix(date).isSame(new DateTime(), 'day') ? onlyTimeFormat : dateTimeFormat
    return formatDate(date, format)
  }
  return '—'
}

export const isoDate = (date: DateValue) => {
  return formatDate(date, isoDateFormat)
}

export const monthDay = (date: DateValue) => {
  return formatDate(date, dateFormat)
}

export const objectDeliveryDate = (subject?: Order | PurchaseOrder | ReleaseRequest) => {
  if (subject && subject.delivery_date) {
    let d = subject.delivery_date

    if (typeof d === 'number') {
      d = DateTime.fromUnix(d)
    }

    const sameYear = d.isSame(new DateTime(), 'year')

    if (subject.delivery_time === 'sp') {
      return d.format(sameYear ? dateTimeNoYearFormat : dateTimeFormat)
    }

    const dateBase = sameYear ? dateFormat : dateYearFormat
    return d.format(`${dateBase} [(${subject.delivery_time === 'am' ? 'Morning' : 'Afternoon'})]`)
  }

  return '—'
}

export const onlyTime = (date: DateValue) => {
  return formatDate(date, onlyTimeFormat)
}
